import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ProgressPhase {
  name: string;
  startPercent: number;
  endPercent: number;
  weight: number;
}

export interface ProgressUpdate {
  phase: string;
  subProgress: number; // 0-100 within the phase
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProgressService {
  private overallProgressSubject = new BehaviorSubject<number>(0);
  private currentPhaseSubject = new BehaviorSubject<string>('');
  private progressMessageSubject = new BehaviorSubject<string>('');

  public overallProgress$ = this.overallProgressSubject.asObservable();
  public currentPhase$ = this.currentPhaseSubject.asObservable();
  public progressMessage$ = this.progressMessageSubject.asObservable();

  private phases: Map<string, ProgressPhase> = new Map([
    ['user-settings', { name: 'User Settings', startPercent: 0, endPercent: 10, weight: 10 }],
    ['project-setup', { name: 'Project Setup', startPercent: 0, endPercent: 15, weight: 15 }],
    ['assembly-loading', { name: 'Assembly Loading', startPercent: 15, endPercent: 50, weight: 35 }],
    ['optimization-data', { name: 'Optimization Data', startPercent: 50, endPercent: 100, weight: 50 }]
  ]);

  private currentPhase: string = '';
  private taskProgress: Map<string, number> = new Map();
  private taskWeights: Map<string, number> = new Map();

  /**
   * Start a new phase of loading
   */
  startPhase(phaseKey: string, message?: string): void {
    const phase = this.phases.get(phaseKey);
    if (!phase) {
      console.warn(`Unknown phase: ${phaseKey}`);
      return;
    }

    
    this.currentPhase = phaseKey;
    this.currentPhaseSubject.next(phase.name);
    this.progressMessageSubject.next(message || phase.name);
    this.overallProgressSubject.next(phase.startPercent);
  }

  /**
   * Update progress within the current phase
   * @param subProgress Progress within current phase (0-100)
   * @param message Optional progress message
   */
  updateProgress(subProgress: number, message?: string): void {
    if (!this.currentPhase) {
      console.warn('No active phase for progress update');
      return;
    }

    const phase = this.phases.get(this.currentPhase);
    if (!phase) return;

    // Clamp subProgress to 0-100
    subProgress = Math.max(0, Math.min(100, subProgress));

    // Calculate overall progress
    const phaseRange = phase.endPercent - phase.startPercent;
    const overallProgress = phase.startPercent + (phaseRange * subProgress / 100);


    this.overallProgressSubject.next(Math.round(overallProgress * 10) / 10); // Round to 1 decimal

    if (message) {
      this.progressMessageSubject.next(message);
    }
  }

  /**
   * Complete the current phase
   */
  completePhase(): void {
    if (!this.currentPhase) return;

    const phase = this.phases.get(this.currentPhase);
    if (phase) {
      this.overallProgressSubject.next(phase.endPercent);
    }
  }

  /**
   * Reset progress to initial state
   */
  reset(): void {
    this.currentPhase = '';
    this.overallProgressSubject.next(0);
    this.currentPhaseSubject.next('');
    this.progressMessageSubject.next('');
  }

  /**
   * Get current overall progress
   */
  getCurrentProgress(): number {
    return this.overallProgressSubject.value;
  }

  /**
   * Get current phase name
   */
  getCurrentPhase(): string {
    return this.currentPhaseSubject.value;
  }

  /**
   * Get current progress message
   */
  getCurrentMessage(): string {
    return this.progressMessageSubject.value;
  }

  /**
   * Update progress message without changing progress
   */
  updateMessage(message: string): void {
    this.progressMessageSubject.next(message);
  }

  /**
   * Start tracking a parallel task within the current phase
   */
  startTask(taskId: string, weight: number, message?: string): void {
    this.taskProgress.set(taskId, 0);
    this.taskWeights.set(taskId, weight);
    this.updateTaskProgress(taskId, 0, message);
  }

  /**
   * Update progress for a specific parallel task
   */
  updateTaskProgress(taskId: string, progress: number, message?: string): void {
    if (!this.currentPhase) {
      console.warn(`No active phase for task progress update: ${taskId}`);
      return;
    }

    const phase = this.phases.get(this.currentPhase);
    if (!phase) return;

    // Clamp progress to 0-100
    progress = Math.max(0, Math.min(100, progress));
    this.taskProgress.set(taskId, progress);

    // Calculate weighted average of all tasks
    let totalWeightedProgress = 0;
    let totalWeight = 0;
    
    for (const [id, taskProgress] of this.taskProgress) {
      const weight = this.taskWeights.get(id) || 1;
      // Only count tasks that have been started (weight > 0)
      if (weight > 0) {
        totalWeightedProgress += taskProgress * weight;
        totalWeight += weight;
      }
    }

    // Ensure we don't divide by zero and handle NaN cases
    const averageTaskProgress = totalWeight > 0 ? totalWeightedProgress / totalWeight : 0;
    
    // Convert to overall progress
    const phaseRange = phase.endPercent - phase.startPercent;
    const overallProgress = phase.startPercent + (phaseRange * averageTaskProgress / 100);

    // Ensure overall progress is a valid number
    const finalProgress = isNaN(overallProgress) ? phase.startPercent : Math.round(overallProgress * 10) / 10;


    this.overallProgressSubject.next(finalProgress);

    if (message) {
      this.progressMessageSubject.next(message);
    }
  }

  /**
   * Complete a parallel task
   */
  completeTask(taskId: string, message?: string): void {
    this.updateTaskProgress(taskId, 100, message);
  }

  /**
   * Clear all task progress (call when starting a new phase)
   */
  clearTasks(): void {
    this.taskProgress.clear();
    this.taskWeights.clear();
  }
}
